import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageLayoutComponent } from '../components';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { OutOfOfficeCalendarComponent } from './components/out-of-office-calendar/out-of-office-calendar.component';
import { Button } from '@shui/angular';
import { UserService } from '../services/user.service';
import { OutOfOfficeEntryEditorComponent } from './components/out-of-office-entry-editor/out-of-office-entry-editor.component';
import { OutOfOfficeService } from './services/out-of-office.service';
import {
    CalendarDateSpan,
    CreateOrUpdateOutOfOfficeDelegationPayload,
    OutOfOfficeDelegation,
    OutOfOfficeEntry,
    OutOfOfficeEntryEditorValue
} from './models/out-of-office.model';
import { Paginated } from '../models/paginated.model';
import { catchError, EMPTY, finalize, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { SnackbarService } from '../ui/sh-snackbar/snackbar.service';

@Component({
    selector: 'app-out-of-office',
    standalone: true,
    templateUrl: './out-of-office.component.html',
    imports: [
        PageLayoutComponent,
        TranslatePipe,
        OutOfOfficeCalendarComponent,
        Button,
        OutOfOfficeEntryEditorComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class OutOfOfficeComponent implements OnInit {
    private readonly outOfOfficeService = inject(OutOfOfficeService);
    private readonly userService = inject(UserService);
    private readonly translateService = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly snackbar = inject(SnackbarService);

    protected readonly isLoading = signal<boolean>(false);
    protected readonly isSaving = signal<boolean>(false);
    protected readonly entries = signal<OutOfOfficeEntry[]>([]);
    public readonly dateSpans = computed<CalendarDateSpan[]>(() =>
        this.entries().map((entry) => ({
            startDate: entry.startDate,
            endDate: entry.endDate
        }))
    );

    protected readonly selectedEntryId = signal<string | null>(null);
    protected readonly editorMode = signal<'create' | 'edit'>('edit');
    protected readonly isEditorVisible = signal<boolean>(false);

    protected readonly selectedEntry = computed(() => {
        const id = this.selectedEntryId();
        return this.entries().find((entry) => entry.id === id) ?? null;
    });

    public ngOnInit(): void {
        this.loadEntries();
    }

    protected onSelectEntry(entryId: string): void {
        this.selectedEntryId.set(entryId);
        this.editorMode.set('edit');
        this.isEditorVisible.set(true);
    }

    protected onCreateEntry(): void {
        this.selectedEntryId.set(null);
        this.editorMode.set('create');
        this.isEditorVisible.set(true);
    }

    protected onUpdateEntry(value: OutOfOfficeEntryEditorValue): void {
        const coverageGid = value.user.trim();
        if (value.dateRange.length !== 2 || !coverageGid) {
            return;
        }

        const [startDate, endDate] = value.dateRange;
        const payload = this.buildPayload(startDate, endDate, coverageGid);

        if (this.editorMode() === 'create') {
            this.createEntry(payload, startDate, endDate, coverageGid);
        } else {
            this.updateEntry(payload, startDate, endDate, coverageGid);
        }
    }

    protected onDeleteEntry(): void {
        const selectedId = this.selectedEntryId();
        if (!selectedId) {
            return;
        }

        const previousEntries = this.entries();
        this.entries.update((entries) => entries.filter((entry) => entry.id !== selectedId));

        const nextEntry = this.entries()[0] ?? null;
        this.selectedEntryId.set(nextEntry?.id ?? null);
        this.editorMode.set('edit');
        this.isEditorVisible.set(Boolean(nextEntry));

        this.outOfOfficeService
            .deleteMyDelegation(selectedId)
            .pipe(
                tap(() => this.snackbar.success('delegation-manager.delete-success')),
                catchError(() => {
                    this.entries.set(previousEntries);
                    this.selectedEntryId.set(selectedId);
                    this.editorMode.set('edit');
                    this.isEditorVisible.set(true);
                    this.snackbar.error('delegation-manager.delete-error');
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private createEntry(
        payload: CreateOrUpdateOutOfOfficeDelegationPayload,
        startDate: Date,
        endDate: Date,
        coverageGid: string
    ): void {
        const tempId = `temp-${Date.now()}`;
        const optimisticEntry = this.buildEntry(tempId, startDate, endDate, coverageGid, coverageGid);
        this.entries.update((entries) => this.sortEntriesByDate([...entries, optimisticEntry]));
        this.isEditorVisible.set(false);
        this.isSaving.set(true);

        this.outOfOfficeService
            .createMyDelegation(payload)
            .pipe(
                switchMap((delegation) => this.resolveEntry(delegation)),
                tap((entry) => {
                    this.entries.update((entries) =>
                        this.sortEntriesByDate(entries.map((e) => (e.id === tempId ? entry : e)))
                    );
                    this.snackbar.success('delegation-manager.create-success');
                }),
                catchError(() => {
                    this.entries.update((entries) => entries.filter((e) => e.id !== tempId));
                    this.isEditorVisible.set(true);
                    this.snackbar.error('delegation-manager.create-error');
                    return EMPTY;
                }),
                finalize(() => this.isSaving.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private updateEntry(
        payload: CreateOrUpdateOutOfOfficeDelegationPayload,
        startDate: Date,
        endDate: Date,
        coverageGid: string
    ): void {
        const selectedId = this.selectedEntryId();
        if (!selectedId) {
            return;
        }

        const previousEntries = this.entries();
        this.entries.update((entries) =>
            this.sortEntriesByDate(
                entries.map((e) =>
                    e.id === selectedId
                        ? { ...e, startDate, endDate, coverageGid, dateRange: this.formatDateRange(startDate, endDate) }
                        : e
                )
            )
        );
        this.selectedEntryId.set(null);
        this.isSaving.set(true);

        this.outOfOfficeService
            .updateMyDelegation(selectedId, payload)
            .pipe(
                switchMap((delegation) => this.resolveEntry(delegation)),
                tap((updatedEntry) => {
                    this.entries.update((entries) =>
                        this.sortEntriesByDate(
                            entries.map((e) => (e.id === selectedId ? { ...e, ...updatedEntry } : e))
                        )
                    );
                    this.snackbar.success('delegation-manager.update-success');
                }),
                catchError(() => {
                    this.entries.set(previousEntries);
                    this.selectedEntryId.set(selectedId);
                    this.snackbar.error('delegation-manager.update-error');
                    return EMPTY;
                }),
                finalize(() => this.isSaving.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private loadEntries(): void {
        const year = new Date().getFullYear();
        const startDate = new Date(year, 0, 1, 0, 0, 0, 0).toISOString();
        const endDate = new Date(year, 11, 31, 23, 59, 59, 999).toISOString();

        this.isLoading.set(true);
        this.outOfOfficeService
            .getMyDelegations({ startDate, endDate })
            .pipe(
                switchMap((response: Paginated<OutOfOfficeDelegation>) =>
                    response.items.length === 0
                        ? of([] as OutOfOfficeEntry[])
                        : forkJoin(response.items.map((delegation) => this.resolveEntry(delegation)))
                ),
                map((entries) => this.sortEntriesByDate(entries)),
                tap((entries) => {
                    this.entries.set(entries);
                    this.selectedEntryId.set(null);
                    this.editorMode.set('edit');
                    this.isEditorVisible.set(false);
                }),
                catchError((error) => {
                    console.error('Failed to load out-of-office delegations', error);
                    return EMPTY;
                }),
                finalize(() => this.isLoading.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private resolveEntry(delegation: OutOfOfficeDelegation): Observable<OutOfOfficeEntry> {
        const startDate = new Date(delegation.startDate);
        const endDate = new Date(delegation.endDate);
        const baseEntry = this.buildEntry(
            delegation.id,
            startDate,
            endDate,
            delegation.coverageGid,
            delegation.coverageGid
        );

        return this.userService.getByGID(delegation.coverageGid).pipe(
            map((user) => ({
                ...baseEntry,
                user: `${user.firstName} ${user.lastName}`.trim() || delegation.coverageGid
            })),
            catchError(() => of(baseEntry))
        );
    }

    private buildEntry(
        id: string,
        startDate: Date,
        endDate: Date,
        coverageGid: string,
        user: string
    ): OutOfOfficeEntry {
        return { id, startDate, endDate, coverageGid, dateRange: this.formatDateRange(startDate, endDate), user };
    }

    private buildPayload(
        startDate: Date,
        endDate: Date,
        coverageGid: string
    ): CreateOrUpdateOutOfOfficeDelegationPayload {
        return {
            coverageGid,
            startDate: new Date(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate(),
                0,
                0,
                0,
                0
            ).toISOString(),
            endDate: new Date(
                endDate.getFullYear(),
                endDate.getMonth(),
                endDate.getDate(),
                23,
                59,
                59,
                999
            ).toISOString(),
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
    }

    private formatDateRange(startDate: Date, endDate: Date): string {
        const locale = this.translateService.currentLang || this.translateService.getDefaultLang() || 'en';
        const formatter = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' });
        return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
    }

    private sortEntriesByDate(entries: OutOfOfficeEntry[]): OutOfOfficeEntry[] {
        return [...entries].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    }
}
